/** Interactive equipped-skills panel used in attunement screens for slot management. */
import Phaser from 'phaser';
import type { AttunementComponent } from '../components/AttunementComponent';
import { ConfigLoader } from '../systems/ConfigLoader';
import { crispText, crispRect } from './CrispUtil';
import { Font, Hex, SLOT_PANEL_BG_ALPHA } from './Theme';
import { GAME_WIDTH } from '../utils/Constants';

const SLOT_COUNT = 4;

export interface SlotPanelCallbacks {
  onSlotClick: (slotIndex: number, skillId: string) => void;
  onSlotAssign: (slotIndex: number, skillId: string) => void;
  onRefreshNeeded: () => void;
}

/**
 * Reusable slot management panel showing equipped skills (V4: 4 hotbar slots).
 * All slots are fully assignable — no fixed foundation slots.
 * When a pending skill is set, empty slots highlight as assignment targets.
 */
export class SlotPanel {
  private scene: Phaser.Scene;
  private attunement: AttunementComponent;
  private callbacks: SlotPanelCallbacks;

  private y: number;
  private slotSpacing: number;
  private group: Phaser.GameObjects.Text[] = [];
  private slotsRemainingText: Phaser.GameObjects.Text | null = null;
  private bg: Phaser.GameObjects.Rectangle | null = null;
  private pendingSkillId: string | null = null;

  /**
   * Creates the static panel chrome (background rect, "HOTBAR" header, and key labels [1]–[4]).
   * Dynamic slot labels are populated by the first call to rebuild().
   */
  constructor(
    scene: Phaser.Scene,
    y: number,
    attunement: AttunementComponent,
    callbacks: SlotPanelCallbacks,
  ) {
    this.scene = scene;
    this.y = y;
    this.attunement = attunement;
    this.callbacks = callbacks;
    this.slotSpacing = GAME_WIDTH / (SLOT_COUNT + 1);

    // Background panel (compact)
    this.bg = crispRect(scene, GAME_WIDTH / 2, y + 8, GAME_WIDTH - 40, 40, 0x000000, SLOT_PANEL_BG_ALPHA)
      .setOrigin(0.5);

    // Static header
    crispText(scene, GAME_WIDTH / 2, y - 5, '— HOTBAR —', {
      fontFamily: Font.family, fontSize: Font.sizeCaption, color: Hex.textSecondary,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5);

    // Slot key labels [1]-[4]
    const slotKeyStyle = {
      fontFamily: Font.family, fontSize: Font.sizeXs, color: Hex.textMuted,
    } as Phaser.Types.GameObjects.Text.TextStyle;
    for (let i = 0; i < SLOT_COUNT; i++) {
      const x = this.slotSpacing * (i + 1);
      crispText(scene, x, y + 2, `[${i + 1}]`, slotKeyStyle).setOrigin(0.5);
    }
  }

  /** Set a pending skill waiting for slot assignment. Empty slots become clickable targets. */
  setPendingSkill(skillId: string | null): void {
    this.pendingSkillId = skillId;
    this.rebuild();
  }

  /** True when a skill is waiting for a slot to be selected by the player. */
  get hasPending(): boolean {
    return this.pendingSkillId !== null;
  }

  /** Rebuild all dynamic slot labels. */
  rebuild(): void {
    for (const t of this.group) t.destroy();
    this.group = [];
    if (this.slotsRemainingText) {
      this.slotsRemainingText.destroy();
      this.slotsRemainingText = null;
    }

    const slotStyle = { fontFamily: Font.family, fontSize: Font.sizeCaption };

    for (let i = 0; i < SLOT_COUNT; i++) {
      const x = this.slotSpacing * (i + 1);
      const skillId = this.attunement.getSkillAtSlot(i);

      let label: string;
      let color: string;

      if (skillId) {
        const def = ConfigLoader.getV4Node(skillId);
        label = def?.name ?? skillId;
        color = Hex.cyan;
      } else if (this.pendingSkillId) {
        label = '[assign]';
        color = '#ffdd00';
      } else {
        label = '[empty]';
        color = Hex.textDisabled;
      }

      const text = crispText(this.scene, x, this.y + 14, label, { ...slotStyle, color })
        .setOrigin(0.5);

      if (this.pendingSkillId && !skillId) {
        text.setInteractive({ useHandCursor: true });
        const slotIndex = i;
        const pending = this.pendingSkillId;
        text.on('pointerdown', () => this.callbacks.onSlotAssign(slotIndex, pending));
        text.on('pointerover', () => text.setColor('#ffffff'));
        text.on('pointerout', () => text.setColor('#ffdd00'));
      } else if (skillId) {
        text.setInteractive({ useHandCursor: true });
        const slotIndex = i;
        text.on('pointerdown', () => this.callbacks.onSlotClick(slotIndex, skillId));
        text.on('pointerover', () => text.setColor('#ff6644'));
        text.on('pointerout', () => this.rebuild());
      }

      this.group.push(text);
    }

    // Slots counter
    const slotsStyle = {
      fontFamily: Font.family, fontSize: Font.sizeXs, color: Hex.cyan,
    } as Phaser.Types.GameObjects.Text.TextStyle;

    let counterText = `Slots: ${this.attunement.availableSlots} / ${SLOT_COUNT} available`;
    if (this.pendingSkillId) {
      const def = ConfigLoader.getV4Node(this.pendingSkillId);
      counterText = `Click a slot to assign ${def?.name ?? this.pendingSkillId}`;
    }

    this.slotsRemainingText = crispText(this.scene, GAME_WIDTH / 2, this.y + 26,
      counterText, slotsStyle,
    ).setOrigin(0.5);
  }

  /** Destroy all panel elements and free resources. */
  destroy(): void {
    for (const t of this.group) t.destroy();
    this.group = [];
    if (this.slotsRemainingText) {
      this.slotsRemainingText.destroy();
      this.slotsRemainingText = null;
    }
    if (this.bg) {
      this.bg.destroy();
      this.bg = null;
    }
  }
}
